/**
 * ChatPage — Soporte mishiwoof. Vive bajo PortalLayout.
 *
 * Layout split: lista de hilos a la izquierda, conversación a la derecha.
 * Burbujas tipo WhatsApp limpias (primary alineado derecha, muted izquierda).
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import {
  useChatThreads,
  useChatMessages,
  useCreateThread,
  useSendMessage,
} from '@modules/chat/presentation/hooks/useChat';
import { useAuth } from '@modules/auth/presentation/hooks/useAuth';
import { getErrorMessage } from '@shared/infrastructure/http/api.error';
import { toast } from 'sonner';
import { cn } from '@shared/utils/cn';
import { ArrowLeft, MessageCircle, Paperclip, Send, Plus } from 'lucide-react';

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function ChatPage() {
  const { user } = useAuth();
  const { data: threads = [], isLoading: loadingThreads } = useChatThreads();
  const createThread = useCreateThread();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [openNew, setOpenNew] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: messages = [], isLoading: loadingMessages } = useChatMessages(selectedThreadId);
  const sendMessage = useSendMessage(selectedThreadId ?? '');
  const selectedThread = threads.find((t) => t.id === selectedThreadId);

  // Autoseleccionar el primer hilo SOLO en desktop. En mobile dejamos que el
  // usuario elija — así no le quitamos el panel de lista al entrar.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (isDesktop && !selectedThreadId && threads.length > 0) {
      setSelectedThreadId(threads[0].id);
    }
  }, [threads, selectedThreadId]);

  // Scroll al final cuando llegan mensajes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleCreateThread = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSubject.trim()) {
      toast.error('Escribe un asunto');
      return;
    }
    try {
      const thread = await createThread.mutateAsync({ subject: newSubject.trim() });
      setNewSubject('');
      setOpenNew(false);
      setSelectedThreadId(thread.id);
      toast.success('Conversación creada');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedThreadId || !newMessage.trim()) return;
    try {
      await sendMessage.mutateAsync({ message: newMessage.trim() });
      setNewMessage('');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Chat" subtitle="Soporte directo desde mishiwoof" />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_1fr]">
        {/* Lista de hilos — en mobile se oculta cuando hay thread seleccionado */}
        <aside
          className={cn(
            'rounded-2xl border border-border bg-card p-4 shadow-xs',
            selectedThreadId && 'hidden md:block',
          )}
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Conversaciones</p>
            <Dialog open={openNew} onOpenChange={setOpenNew}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-8 rounded-full" aria-label="Nueva conversación">
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Nueva conversación</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateThread} className="mt-2 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="subject">Asunto</Label>
                    <Input
                      id="subject"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      placeholder="Ej: Consulta sobre mi plan"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={createThread.isPending}
                  >
                    {createThread.isPending ? 'Creando…' : 'Crear conversación'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loadingThreads ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-xl" />
              ))}
            </div>
          ) : threads.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              Sin conversaciones. Toca <span className="font-medium">+</span> para empezar.
            </p>
          ) : (
            <ul className="space-y-1">
              {threads.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedThreadId(t.id)}
                    className={cn(
                      'w-full rounded-xl px-3 py-2 text-left text-sm transition-colors',
                      selectedThreadId === t.id
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <p className="truncate font-medium">{t.subject}</p>
                    {t.last_message_at && (
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {formatTime(t.last_message_at)}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Conversación — en mobile se oculta si no hay selección (mostramos solo lista) */}
        <section
          className={cn(
            'flex min-h-[480px] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-xs',
            !selectedThreadId && 'hidden md:flex',
          )}
        >
          {!selectedThreadId ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <EmptyState
                icon={MessageCircle}
                title="Sin conversación activa"
                description="Selecciona una conversación o crea una nueva para empezar."
              />
            </div>
          ) : (
            <>
              <header className="flex items-center gap-2 border-b border-border bg-card px-3 py-3 sm:px-5 sm:py-3.5">
                {/* Botón volver (solo mobile) */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-full md:hidden"
                  onClick={() => setSelectedThreadId(null)}
                  aria-label="Volver a conversaciones"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                </Button>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <MessageCircle className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">
                    {selectedThread?.subject ?? 'Conversación'}
                  </p>
                  <p className="text-xs text-muted-foreground">Soporte mishiwoof</p>
                </div>
              </header>

              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto bg-background/60 p-4"
              >
                {loadingMessages ? (
                  <div className="space-y-3">
                    <Skeleton className="h-12 w-[60%] rounded-2xl" />
                    <Skeleton className="ml-auto h-12 w-[55%] rounded-2xl" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    Sé el primero en escribir. Te respondemos rápido.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {messages.map((m) => {
                      const mine = m.sender_id === user?.user_id;
                      return (
                        <li
                          key={m.id}
                          className={cn('flex w-full', mine ? 'justify-end' : 'justify-start')}
                        >
                          <div className="max-w-[85%] space-y-1">
                            <div
                              className={cn(
                                'rounded-2xl px-3.5 py-2 text-sm shadow-xs',
                                mine
                                  ? 'rounded-br-md bg-primary text-primary-foreground'
                                  : 'rounded-bl-md bg-muted text-foreground',
                              )}
                            >
                              {m.message}
                            </div>
                            <p
                              className={cn(
                                'text-[10px] text-muted-foreground',
                                mine ? 'text-right' : 'text-left',
                              )}
                            >
                              {formatTime(m.created_at)}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <form
                onSubmit={handleSendMessage}
                className="flex items-center gap-2 border-t border-border bg-card px-3 py-3"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-full text-muted-foreground"
                  aria-label="Adjuntar archivo (próximamente)"
                  disabled
                  title="Adjuntar archivo (próximamente)"
                >
                  <Paperclip className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Escribe un mensaje…"
                  className="flex-1 rounded-full bg-background"
                />
                <Button
                  type="submit"
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-full"
                  disabled={sendMessage.isPending || !newMessage.trim()}
                  aria-label="Enviar mensaje"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
